import _ from 'underscore';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useCouponContext } from '@bingads-webui-campaign-react/coupon-context';
import {
  actions, couponState, couponUserActions, initialState, useCouponProcessorReducer,
} from './use-coupon-processor-reducer';
import { usePromotionalOfferError } from './use-promotional-offer-error';

// UseCouponProcessor processes the resolved coupon and user actions to view states,
// 1. User Actions - Coupon Add, Coupon Remove
// 2. Coupon states - noCoupon, coupon editing, coupon is resolving, coupon success, coupon error

export const useCouponProcessor = ({ coupon }) => {
  const {
    couponDetails,
    couponValue,
    isLoading,
  } = coupon || {};
  const { couponString } = couponValue || {};

  const getCouponServerError = usePromotionalOfferError();
  const [state, dispatch] = useReducer(useCouponProcessorReducer, initialState);
  const { resetCouponStore } = useCouponContext() || {};

  const couponEditingDispatch = useCallback((isValid, newCoupon) => dispatch({
    type: actions.COUPON_EDITING,
    newCoupon,
    isValid,
    coupon,
  }), [coupon]);

  const onCouponAction = useCallback((type) => {
    if (type === couponUserActions.add) {
      dispatch({ type: actions.COUPON_ADD_ACTION, coupon });
    } else {
      dispatch({ type: actions.COUPON_REMOVE_ACTION });
    }
  }, [coupon]);

  const isUserActionDisabled = useCallback((type) => {
    if (state.isResolving) {
      return true;
    }
    if (type === couponUserActions.add) {
      return ((_.isEmpty(couponString) && _.isEmpty(state.newCoupon)) ||
      !state.isInputCodeValid || state.serverError ||
      (state.couponStatus === couponState.couponEditing && _.isEmpty(state.newCoupon)));
    }
    return (_.isEmpty(couponString) || state.couponStatus === couponState.couponEditing);
  }, [couponString,
    state.couponStatus,
    state.isInputCodeValid,
    state.isResolving,
    state.newCoupon,
    state.serverError]);

  const onValidate = useCallback((result) => {
    if (state.isInputCodeValid === result.isValid) {
      return;
    }
    couponEditingDispatch(result.isValid, state.newCoupon);
  }, [couponEditingDispatch, state.isInputCodeValid, state.newCoupon]);

  const onCouponCodeChange = useCallback(({ target: { value } }) => {
    if (_.isEmpty(value) && !_.isEmpty(couponString) && _.isFunction(resetCouponStore)) {
      // User deleted a coupon code entry, clear the coupon store
      // so that any refresh of the page doesn't reload the stale value from store
      resetCouponStore();
    }
    couponEditingDispatch(state.isInputCodeValid, value);
  }, [couponEditingDispatch, couponString, state.isInputCodeValid, resetCouponStore]);

  const isCouponValidAndResolved = useMemo(
    () => (!isLoading &&
       !_.isEmpty(couponDetails) &&
       state.couponStatus === couponState.couponSuccessful),
    [isLoading, couponDetails, state.couponStatus]
  );

  const couponDisplayCode = useMemo(() => {
    if (state.couponStatus === couponState.couponError ||
      state.couponStatus === couponState.couponSuccessful) {
      return !_.isEmpty(couponString) ? couponString : null;
    }
    return state.newCoupon ? state.newCoupon : null;
  }, [couponString, state.couponStatus, state.newCoupon]);

  useEffect(() => {
    if (isLoading ||
      !state.isInputCodeValid ||
      (!state.isResolving && state.couponStatus === couponState.couponEditing)) {
      return;
    }

    const error = getCouponServerError(coupon);
    if (error !== state.serverError) {
      dispatch({
        type: actions.COUPON_RESOLVED_ERROR,
        error,
        coupon,
      });
    } else if (_.isEmpty(couponString) &&
      _.isEmpty(state.newCoupon) &&
      !error &&
      state.couponStatus !== couponState.noCoupon) {
      dispatch({ type: actions.NO_COUPON, coupon });
    } else if (!error &&
      !_.isEmpty(couponDetails) &&
      state.couponStatus !== couponState.couponSuccessful) {
      dispatch({
        type: actions.COUPON_RESOLVED_SUCCESS,
        coupon,
      });
    }
  }, [getCouponServerError,
    coupon,
    couponDetails,
    couponString,
    couponValue,
    dispatch,
    isLoading,
    state.couponStatus,
    state.isInputCodeValid,
    state.isResolving,
    state.newCoupon,
    state.resolvedCoupon,
    state.serverError]);

  return [{
    state,
    isCouponValidAndResolved,
    couponDisplayCode,
  }, {
    onValidate,
    onCouponCodeChange,
    onCouponAction,
    isUserActionDisabled,
  }];
};
